import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import RankRow from "@/components/feature/RankRow";
import usePageMeta from "@/hooks/usePageMeta";
import { categories } from "@/mocks/guaData";
import { getRankedTopics, type RankPeriod } from "@/utils/content";

const SITE_URL = "https://0h3yz1.cn";

const periodOptions: { key: RankPeriod; label: string; hint: string }[] = [
  { key: "day", label: "24小时", hint: "近一天讨论增速最快的话题" },
  { key: "week", label: "本周", hint: "本周累计热度最高的话题" },
  { key: "month", label: "本月", hint: "本月持续被讨论的话题" },
];

export default function RankPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<RankPeriod>("day");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");

  usePageMeta({
    title: "今日热门吃瓜榜 - 热门吃瓜事件排行 | 瓜田热榜",
    description:
      "今日热门吃瓜榜完整榜单，支持 24 小时、本周、本月三个维度切换，并可按娱乐八卦、影视综、科技数码等分类筛选，快速定位当前最热门的吃瓜话题。",
    keywords: "今日热门吃瓜榜,热门吃瓜事件排行,热门吃瓜热搜合集,吃瓜榜单,全网热议",
    canonical: `${SITE_URL}/rank`,
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "今日热门吃瓜榜",
        url: `${SITE_URL}/rank`,
        inLanguage: "zh-CN",
        description: "实时更新的热门吃瓜事件排行，覆盖娱乐八卦、影视综、科技数码等六大分类。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "今日热门吃瓜榜", item: `${SITE_URL}/rank` },
        ],
      },
    ],
  });

  const ranked = useMemo(() => getRankedTopics(period), [period]);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return ranked.filter((topic) => {
      const matchCategory = categoryFilter === "all" || topic.categorySlug === categoryFilter;
      if (!matchCategory) return false;
      if (!keyword) return true;
      const haystack = [topic.title, topic.summary, ...topic.tags].join(" ").toLowerCase();
      return haystack.includes(keyword);
    });
  }, [ranked, categoryFilter, query]);

  const activePeriod = periodOptions.find((option) => option.key === period);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (value.trim()) {
      setSearchParams({ q: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const resetFilters = () => {
    setCategoryFilter("all");
    handleQueryChange("");
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <Breadcrumb items={[{ label: "今日热门吃瓜榜" }]} />

      <main className="w-full">
        <section className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
            <div className="max-w-3xl">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">
                <span className="h-px w-6 bg-primary-400" />
                榜单中心
              </p>
              <h1 className="heading-font mt-3 text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                今日热门吃瓜榜
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                综合站内阅读、评论互动量与讨论增速实时排序。榜单每小时刷新，你也可以按时间和分类把范围收窄，
                只看自己关心的那一块。
              </p>
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-1 rounded-full bg-background-200/70 px-1 py-1" role="tablist" aria-label="榜单时间范围">
                  {periodOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      role="tab"
                      aria-selected={period === option.key}
                      onClick={() => setPeriod(option.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                        period === option.key
                          ? "bg-background-50 text-foreground-950"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <label className="flex h-11 w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400 lg:max-w-sm">
                  <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                    <i className="ri-search-line text-base leading-none" aria-hidden="true" />
                  </span>
                  <span className="sr-only">搜索话题</span>
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => handleQueryChange(event.target.value)}
                    placeholder="搜索话题、关键词或标签"
                    className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                  />
                  {query && (
                    <button
                      type="button"
                      aria-label="清空搜索"
                      onClick={() => handleQueryChange("")}
                      className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full text-foreground-500 hover:text-foreground-900"
                    >
                      <i className="ri-close-line text-base leading-none" aria-hidden="true" />
                    </button>
                  )}
                </label>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
                <span className="text-xs text-foreground-500">分类：</span>
                <button
                  type="button"
                  onClick={() => setCategoryFilter("all")}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    categoryFilter === "all"
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200/70 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  全部
                </button>
                {categories.map((category) => (
                  <button
                    key={category.slug}
                    type="button"
                    onClick={() => setCategoryFilter(category.slug)}
                    className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      categoryFilter === category.slug
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-200/70 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    <i className={`${category.icon} text-sm leading-none`} aria-hidden="true" />
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            <p className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-500">
              <span>
                当前范围：<strong className="text-foreground-800">{activePeriod?.label}</strong>
                {categoryFilter !== "all" && (
                  <>
                    {" · "}
                    <strong className="text-foreground-800">
                      {categories.find((item) => item.slug === categoryFilter)?.name}
                    </strong>
                  </>
                )}
              </span>
              <span className="text-foreground-400">|</span>
              <span>共 {filtered.length} 个话题</span>
              {query && (
                <span className="text-foreground-400">关键词“{query}”</span>
              )}
            </p>

            {filtered.length > 0 ? (
              <div className="mt-3 rounded-lg border border-background-200 bg-background-50 p-2 md:p-3">
                <div className="hidden items-center justify-between px-4 pb-2 pt-2 text-xs uppercase tracking-widest text-foreground-400 md:flex">
                  <span>排名 / 话题</span>
                  <span>热度 / 走势</span>
                </div>
                <div className="flex flex-col divide-y divide-background-200">
                  {filtered.map((topic, index) => (
                    <RankRow key={topic.id} topic={topic} rank={index + 1} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-3 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-emotion-normal-line text-2xl leading-none" aria-hidden="true" />
                </span>
                <h2 className="heading-font mt-4 text-lg font-bold text-foreground-950">
                  暂时没有匹配的话题
                </h2>
                <p className="mt-2 max-w-md text-sm text-foreground-600">
                  换个关键词，或者把分类恢复成“全部”，再看看这个时间段里还有哪些话题在发酵。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  重置筛选条件
                </button>
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                <i className="ri-lightbulb-line text-lg leading-none" aria-hidden="true" />
              </span>
              <p className="flex-1 text-sm text-foreground-600">
                榜单反映的是当下的讨论热度，不等于事件本身的轻重。想快速看懂某个话题，可以进入详情页查看事件脉络。
              </p>
              <Link
                to="/about"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                内容原则
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-base leading-none" aria-hidden="true" />
                </span>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}